import {
  IconArrowLeft,
  IconBulb,
  IconBuildingBank,
  IconCheck,
  IconLock,
  IconPlayerPlay,
} from "@tabler/icons-react";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { analytics } from "../../analytics/analyticsEvents";
import { getClientById, getContractById } from "../../content";
import { useLocalize } from "../../content/useLocalize";
import {
  DEFAULT_CLIENT_TRUST,
  baseRewardForDifficulty,
  isContractUnlocked,
  REPLAY_REWARD_MULTIPLIER,
  TRUSTED_CLIENT_TRUST,
  trustedClientBonus,
} from "../../engine/progression";
import { rewardRange } from "../../engine/scoring";
import { contractTypeText } from "../../i18n/enumText";
import { taskDescriptionText, taskHintText } from "../../i18n/taskHints";
import { difficultyTooltip, rewardTooltip, trustTooltip } from "../../i18n/tooltips";
import { isUrgentActive } from "../../engine/contractOffers";
import { useGameStore } from "../../state/store";
import { Button } from "../common/Button";
import { Card } from "../common/Card";
import { DifficultyMeter } from "../common/DifficultyMeter";
import { EmptyState } from "../common/EmptyState";
import { Panel } from "../common/Panel";
import { StatTile } from "../common/StatTile";
import { Tooltip } from "../common/Tooltip";
import { cx } from "../common/cx";
import { formatMoney, formatNumber } from "../common/format";
import { ContractRiskBadge } from "./ContractRiskBadge";
import { RewardGradeHint } from "./RewardGradeHint";
import { UrgentBanner } from "./UrgentBanner";
import styles from "./ContractDetails.module.css";

export function ContractDetails(): JSX.Element {
  const { i18n } = useLingui();
  const t = useLocalize();
  const activeContractId = useGameStore((state) => state.activeContractId);
  const player = useGameStore((state) => state.player);
  const navigateTo = useGameStore((state) => state.navigateTo);
  const setActiveContract = useGameStore((state) => state.setActiveContract);

  const contract = activeContractId ? getContractById(activeContractId) : undefined;

  if (!contract) {
    return (
      <EmptyState
        title={<Trans>Контракт не выбран</Trans>}
        action={
          <Button variant="secondary" onClick={() => { setActiveContract(null); navigateTo("contracts"); }}>
            <Trans>К контрактам</Trans>
          </Button>
        }
      />
    );
  }

  const clientTrust = useGameStore((state) => state.clientTrust);
  const offers = useGameStore((state) => state.offers);
  const refreshOffersNow = useGameStore((state) => state.refreshOffersNow);
  const urgent =
    offers.urgent && isUrgentActive(offers, contract.id, Date.now()) ? offers.urgent : null;
  const client = getClientById(contract.clientId);
  const reward = contract.reward ?? baseRewardForDifficulty(contract.difficulty);
  const trust = clientTrust[contract.clientId] ?? client?.trust ?? DEFAULT_CLIENT_TRUST;
  const trustBonus = trustedClientBonus(trust);
  const trustBonusPct = Math.round(trustBonus * 100);
  const trustedFrom = TRUSTED_CLIENT_TRUST;
  const moneyR = rewardRange(reward.money * (1 + trustBonus));
  const xpR = rewardRange(reward.xp);
  const repR = rewardRange(reward.reputation);
  const unlocked = isContractUnlocked(contract, player);
  const completed = player.completedContracts.includes(contract.id);
  const requiredRep = contract.requiredReputation;
  const reputation = player.reputation;

  return (
    <div className={styles.layout}>
      <div className={styles.main}>
        <button type="button" className={styles.back} onClick={() => { setActiveContract(null); navigateTo("contracts"); }}>
          <IconArrowLeft size={16} aria-hidden="true" />
          <Trans>К контрактам</Trans>
        </button>

        <div className={styles.header}>
          <h1 className={styles.title}>{t(contract.title)}</h1>
          <div className={styles.sub}>
            {client && <span className={styles.client}>{t(client.name)}</span>}
            <Tooltip content={i18n._(difficultyTooltip())}>
              <DifficultyMeter difficulty={contract.difficulty} />
            </Tooltip>
            {contract.riskLevel && <ContractRiskBadge riskLevel={contract.riskLevel} />}
          </div>
        </div>

        {urgent && (
          <UrgentBanner
            deadlineMs={urgent.deadlineMs}
            durationMs={urgent.durationMs}
            bonusPct={urgent.bonusPct}
            onExpire={refreshOffersNow}
          />
        )}

        <Card>
          <p className={styles.brief}>{t(contract.brief)}</p>
        </Card>

        {contract.description && (
          <Card>
            <p className={styles.brief}>{t(contract.description)}</p>
          </Card>
        )}

        <Panel title={<Trans>Задачи</Trans>}>
          <ol className={styles.tasks}>
            {contract.tasks.map((task, index) => (
              <li key={`${task}-${index}`} className={styles.task}>
                <span className={styles.taskNum}>{index + 1}</span>
                <div className={styles.taskBody}>
                  <span className={styles.taskName}>{i18n._(contractTypeText(task))}</span>
                  <span className={styles.taskDesc}>{i18n._(taskDescriptionText(task))}</span>
                  <span className={styles.taskHintChip}>
                    <IconBulb size={13} aria-hidden="true" />
                    {i18n._(taskHintText(task))}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </Panel>

      </div>

      <aside className={styles.side}>
        <Panel title={<Trans>Награда</Trans>}>
          <div className={styles.rewards}>
            <Tooltip content={i18n._(rewardTooltip())}>
              <StatTile
                label={<Trans>Деньги</Trans>}
                value={`${formatMoney(moneyR.min)} … ${formatMoney(moneyR.max)}`}
                tone="money"
              />
            </Tooltip>
            <Tooltip content={i18n._(rewardTooltip())}>
              <StatTile
                label={<Trans>Опыт</Trans>}
                value={`+${formatNumber(xpR.min)} … +${formatNumber(xpR.max)}`}
              />
            </Tooltip>
            <Tooltip content={i18n._(rewardTooltip())}>
              <StatTile
                label={<Trans>Репутация</Trans>}
                value={`+${formatNumber(repR.min)} … +${formatNumber(repR.max)}`}
              />
            </Tooltip>
            <Tooltip content={i18n._(trustTooltip())}>
              <StatTile
                label={<Trans>Доверие</Trans>}
                value={`${formatNumber(trust)} / 100`}
                icon={<IconBuildingBank size={14} />}
              />
            </Tooltip>
          </div>

          <p className={cx(styles.trustNote, trustBonusPct > 0 && styles.trustNoteOn)}>
            {trustBonusPct > 0 ? (
              <Trans>Доверенный клиент: платит +{trustBonusPct}% к награде.</Trans>
            ) : (
              <Trans>С доверия {trustedFrom} клиент платит больше. Повтор контракта доверие не меняет.</Trans>
            )}
          </p>

          <RewardGradeHint />

          {requiredRep > 0 && (
            <div className={styles.requirement}>
              <span className={cx(styles.reqIcon, unlocked ? styles.ok : styles.bad)}>
                {unlocked ? <IconCheck size={16} /> : <IconLock size={16} />}
              </span>
              <Trans>
                Нужна репутация {requiredRep} · у тебя {reputation}
              </Trans>
            </div>
          )}

          {unlocked ? (
            <span data-tutorial="take-contract">
              <Button
                variant="primary"
                icon={<IconPlayerPlay size={17} />}
                onClick={() => {
                  analytics.contractStarted({
                    contractId: contract.id,
                    difficulty: contract.difficulty,
                  });
                  navigateTo("workspace");
                }}
              >
                {completed ? <Trans>Пройти снова</Trans> : <Trans>Начать контракт</Trans>}
              </Button>
              {completed && (
                <p className={styles.replayNote}>
                  <Trans>
                    Контракт уже пройден: за повтор начисляется ×{REPLAY_REWARD_MULTIPLIER} награды.
                  </Trans>
                </p>
              )}
            </span>
          ) : (
            <>
              <Button variant="secondary" disabled icon={<IconLock size={16} />}>
                <Trans>Пока недоступно</Trans>
              </Button>
              <div className={styles.lockedHint}>
                <p className={styles.lockedReason}>
                  <Trans>
                    Репутация растёт за каждый пройденный контракт: чем выше оценка, тем больше
                    прибавка. Пройди доступные контракты, чтобы открыть этот.
                  </Trans>
                </p>
                <div className={styles.lockedLinks}>
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => {
                      setActiveContract(null);
                      navigateTo("contracts");
                    }}
                  >
                    <Trans>К доступным контрактам</Trans>
                  </Button>
                </div>
              </div>
            </>
          )}
        </Panel>
      </aside>
    </div>
  );
}
